/**
 * Lifecycle State Indicator for Roadmap Items
 * @implements FS-005 Data Requirements - Ticket Lifecycle States
 * @implements US-037 - Basic Roadmap Viewing (Visual States)
 *
 * Displays the lifecycle state of a roadmap item (milestone or epic).
 * Active items have normal styling, canceled items show strikethrough/muted,
 * and archived items appear dimmed.
 */

import { Chip, Tooltip } from '@mui/material';
import {
  Cancel as CancelIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';

/**
 * Roadmap ticket lifecycle state values matching the backend enum
 * @implements FS-005 Data Requirements - Ticket Lifecycle States
 */
export type RoadmapLifecycleState = 'active' | 'canceled' | 'archived';

/**
 * Props for LifecycleIndicator component
 */
export interface LifecycleIndicatorProps {
  /** The lifecycle state to display */
  state: RoadmapLifecycleState;
  /** Optional cancellation reason (only for canceled state) */
  cancellationReason?: string | null;
  /** Size of the indicator */
  size?: 'small' | 'medium';
}

/**
 * LifecycleIndicator - Shows lifecycle state for roadmap items
 *
 * @implements US-037 - Visual States
 *
 * Features:
 * - Active: no indicator shown (normal state)
 * - Canceled: red chip with cancel icon, tooltip shows reason
 * - Archived: gray chip with archive icon
 */
export const LifecycleIndicator = ({
  state,
  cancellationReason,
  size = 'small',
}: LifecycleIndicatorProps) => {
  // Active items don't need an indicator
  if (state === 'active') {
    return null;
  }

  if (state === 'canceled') {
    const chip = (
      <Chip
        label="Canceled"
        size={size}
        color="error"
        variant="outlined"
        icon={<CancelIcon />}
        sx={{ fontWeight: 500, fontSize: '0.75rem' }}
      />
    );

    if (cancellationReason) {
      return <Tooltip title={cancellationReason}>{chip}</Tooltip>;
    }

    return chip;
  }

  if (state === 'archived') {
    return (
      <Chip
        label="Archived"
        size={size}
        color="default"
        variant="outlined"
        icon={<ArchiveIcon />}
        sx={{ fontWeight: 500, fontSize: '0.75rem', opacity: 0.7 }}
      />
    );
  }

  return null;
};
